import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Banner from 'jsx/elements/banners/Banner';
import Button from 'jsx/elements/buttons/Button';
import Flex from 'jsx/layout/Flex';
import Spinner from 'jsx/elements/spinner';
import { usePortfolioTranslation as useLocale } from 'jsx/hooks/useTranslation';
import paymentSvg from 'images/ci-icon-payment.svg';
import { formatNumber } from 'utils/number';

import ExclusionModal from 'components/ExclusionModal';
import { usePortfolio } from 'components/PortfolioProvider';
import { usePaymentBehavior } from 'components/PaymentBehaviorProvider';
import './PaymentBehavior.less';

export default function PaymentBehavior({ monitor }) {
  const [modalOpen, setModalOpen] = React.useState(false);
  const { status, data, selectPortfolioMonitor } = usePortfolio();
  const { t } = useLocale();
  const { status: pbStatus, sharing } = usePaymentBehavior();

  useEffect(() => {
    if (monitor) {
      selectPortfolioMonitor(monitor);
    }
  }, [monitor]);

  const { twoOrMore, totalDebt, debtPastDue } = React.useMemo(() => {
    let twoOrMore = 0;
    let totalDebt = 0;
    let debtPastDue = 0;
    if (data && sharing) {
      data.forEach((item) => {
        if (item.pbsOutstandingCountAll >= 2) {
          twoOrMore += 1;
        }
        totalDebt += item.pbsOutstandingAmountAll || 0;
        debtPastDue += item.pbsOutstandingAmountPastDue || 0;
      });
    }
    return {
      twoOrMore,
      totalDebt,
      debtPastDue,
    };
  }, [data, sharing]);

  if (pbStatus === 'loaded' && !sharing) {
    return (
      <div className="gutter-top">
        <h2>{t('dashboard_header_payment')}</h2>
        <Banner
          image={paymentSvg}
          title={t('paymentBehavior_notSharingBanner_title')}
          main={t('paymentBehavior_notSharingBanner_main')}
          callToAction={
            <a href={'mailto:' + t('paymentBehavior_notSharingBanner_cta_mail')}>
              <Button text={t('paymentBehavior_notSharingBanner_cta_text')} />
            </a>
          }
        />
      </div>
    );
  }

  return (
    <div className="gutter-top">
      {modalOpen && <ExclusionModal onClose={() => setModalOpen(false)} />}
      <Flex justifyContent="space-between">
        <h2>{t('dashboard_header_payment')}</h2>
        <Button
          color="gray-light"
          text={t('paymentBehavior_exclusion_button')}
          onClick={() => setModalOpen(true)}
        />
      </Flex>
      <div className="payment-behavior gutter-bottom">
        {status !== 'loaded' && <Spinner relCenter />}
        <div className="payment-behavior__entry">
          <div className="text-upper text-semibold text-14">{t('paymentBehavior_xormore', 2)}</div>
          <div className="text-bold text-18">
            {data ? (
              twoOrMore > 0 ? (
                <Link to={{ pathname: '/portfolio', search: 'outstandingCountMin=2' }}>
                  {formatNumber(twoOrMore)} {t('paymentBehavior_bodies')}
                </Link>
              ) : (
                <span>
                  {twoOrMore} {t('paymentBehavior_bodies')}
                </span>
              )
            ) : (
              <span>?</span>
            )}
          </div>
        </div>
        <div className="payment-behavior__entry">
          <div className="text-upper text-semibold text-14">
            {t('paymentBehavior_outstanding_debt')}
          </div>
          <div className="text-bold text-18">
            {data ? <span>{formatNumber(totalDebt)} kr.</span> : <span>?</span>}
          </div>
        </div>
        <div className="payment-behavior__entry">
          <div className="text-upper text-semibold text-14">
            {t('paymentBehavior_debts_past_deadline')}
          </div>
          <div className="text-bold text-18">
            {data ? <span>{formatNumber(debtPastDue)} kr.</span> : <span>?</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
